import { useLocalStorage } from "@vueuse/core";
import type { ComputedRef, Ref } from "vue";
import { useApi } from "~/composables/useApi";
import { useEnvironment } from "~/composables/useEnvironment";
import { FEATURE_FLAG_KEYS, type FeatureFlagKey } from "@asap/shared/constants/feature-flags";
import type { FeatureFlags } from "~/utils/feature-flag";
import { seedFeatureFlags } from "~/utils/feature-flag";

const FEATURE_FLAGS_STORAGE_KEY = "feature-flags";

const isInitialized = ref(false);

let featureFlagsRef: Ref<FeatureFlags> | undefined;

const getFeatureFlagsRef = (isNotProduction: boolean) => {
  if (featureFlagsRef) return featureFlagsRef;
  if (isNotProduction) {
    featureFlagsRef = useLocalStorage<FeatureFlags>(FEATURE_FLAGS_STORAGE_KEY, {});
  } else {
    featureFlagsRef = ref<FeatureFlags>({});
  }
  return featureFlagsRef;
};

export const useFeatureFlag = () => {
  const { isNotProduction } = useEnvironment();
  const { invoke } = useApi();
  const featureFlags = getFeatureFlagsRef(isNotProduction);

  /**
   * Initializes the feature flags.
   * Used only once on app mount.
   *
   * Non-production: backed by localStorage; any flag not yet stored defaults to
   * `true`, preserving the historical "everything on in dev" behavior.
   * Production: backed by an in-memory ref and populated from one PostHog batch —
   * localStorage is never touched, so DevTools tampering has no effect.
   */
  const init = async () => {
    if (isInitialized.value) return;

    if (isNotProduction) {
      featureFlags.value = seedFeatureFlags(featureFlags.value);
      isInitialized.value = true;
      return;
    }

    try {
      const response = await invoke({ path: "feature-flags", method: "GET" });

      const flags = response.success ? response.data.flags : {};
      for (const key of FEATURE_FLAG_KEYS) featureFlags.value[key] = flags[key] ?? false;
    } catch {
      for (const key of FEATURE_FLAG_KEYS) featureFlags.value[key] = false;
    }

    isInitialized.value = true;
  };

  const isFeatureFlagEnabled = (key: FeatureFlagKey): ComputedRef<boolean> =>
    computed(() => featureFlags.value[key] ?? false);

  const setFeatureFlag = (key: FeatureFlagKey, value: boolean) => {
    featureFlags.value = { ...featureFlags.value, [key]: value };
  };

  const setAllFeatureFlags = (value: boolean) => {
    const nextFeatureFlags: FeatureFlags = {};
    for (const key of FEATURE_FLAG_KEYS) nextFeatureFlags[key] = value;
    featureFlags.value = nextFeatureFlags;
  };

  return {
    init,
    isFeatureFlagEnabled,
    featureFlags,
    setFeatureFlag,
    setAllFeatureFlags,
    isInitialized,
  };
};
